import { useEffect } from "react";
import "../scss/layout.scss";

/**
 * Prevents page scrolling when modal is open.
 *
 * Scroll-neutral locks avoid fixed-body positioning so virtualized list rows
 * keep their window scroll metrics while a portalled panel is open.
 */
export interface LockBodyScrollingOptions {
  scrollNeutral?: boolean;
}

export const useLockBodyScrolling = (
  shouldLock: boolean,
  modalRef?: React.RefObject<HTMLElement | null>,
  preventTouchMovesCallback?: (e: Event) => void,
  options: LockBodyScrollingOptions = {},
) => {
  const { scrollNeutral = false } = options;

  useEffect(() => {
    if (shouldLock) {
      const horizontalScrollPos = window.scrollY;
      const originalOverflow = document.body.style.overflow;
      const originalTouchAction = document.body.style.touchAction;

      if (scrollNeutral) {
        document.body.style.overflow = "hidden";
        document.body.style.touchAction = "none";
      } else {
        if (horizontalScrollPos > 0) {
          document.body.style.top = `-${horizontalScrollPos}px`;
        }
        document.body.className = "LockScrolling";
      }

      const preventTouchMoves = (e: Event) => {
        if (modalRef?.current && modalRef.current.contains(e.target as Node)) {
          // if touch is inside modal return to allow movement
          return;
        }
        e.preventDefault();
      };

      // on mobile prevent touches from scrolling the background
      document.body.addEventListener("touchmove", preventTouchMovesCallback ?? preventTouchMoves, { passive: false });

      return () => {
        if (scrollNeutral) {
          document.body.style.overflow = originalOverflow;
          document.body.style.touchAction = originalTouchAction;
        } else {
          document.body.style.top = "";
          document.body.className = "";
          window.scrollTo(0, horizontalScrollPos);
        }

        document.body.removeEventListener("touchmove", preventTouchMovesCallback ?? preventTouchMoves);
      };
    }
  }, [shouldLock, modalRef, preventTouchMovesCallback, scrollNeutral]);
};
